import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, X } from 'lucide-react';

interface FormData {
  nome: string;
  telefone: string;
  email: string;
  assunto: string;
}

const ContactForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    telefone: '',
    email: '',
    assunto: ''
  });
  
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro quando o usuário digita
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (!/^[0-9]{10,11}$/.test(formData.telefone.replace(/\D/g, ''))) {
      newErrors.telefone = 'Telefone inválido';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    if (!formData.assunto.trim()) {
      newErrors.assunto = 'Assunto é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Formatar a mensagem para o WhatsApp com emojis
      const message = `Olá Dra. Lidiane, eu vim pelo site: 👋

Gostaria de solicitar as seguintes informações:
      
📋 *Nome*: ${formData.nome}
📱 *Telefone*: ${formData.telefone}
📧 *E-mail*: ${formData.email || 'Não informado'}
💼 *Assunto*: ${formData.assunto}

Aguardo seu retorno. Obrigado(a)! ✅`;
      
      // Codificar a mensagem para URL
      const encodedMessage = encodeURIComponent(message);
      
      // Abrir WhatsApp com a mensagem
      window.open(`https://wa.me/558193507689?text=${encodedMessage}`, '_blank');
      
      // Fechar o formulário
      onClose();
    }
  };

  const formatTelefone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim();
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim();
    }
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatTelefone(e.target.value);
    e.target.value = formattedValue;
    handleChange(e);
  };

  return (
    <motion.div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md relative overflow-hidden"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="bg-primary p-6 flex justify-between items-center">
          <h2 className="text-white font-bold text-xl">Fale com um especialista</h2>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome*</label>
            <input
              type="text"
              id="nome"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-colors text-gray-900 ${
                errors.nome ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Seu nome completo"
            />
            {errors.nome && <p className="mt-1 text-sm text-red-500">{errors.nome}</p>}
          </div>
          
          <div>
            <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">Telefone*</label>
            <input
              type="tel"
              id="telefone"
              name="telefone"
              value={formData.telefone}
              onChange={handleTelefoneChange}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-colors text-gray-900 ${
                errors.telefone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="(00) 00000-0000"
            />
            {errors.telefone && <p className="mt-1 text-sm text-red-500">{errors.telefone}</p>}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">E-mail (opcional)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-colors text-gray-900 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="seu@email.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>
          
          <div>
            <label htmlFor="assunto" className="block text-sm font-medium text-gray-700 mb-1">Assunto*</label>
            <textarea
              id="assunto"
              name="assunto"
              value={formData.assunto}
              onChange={handleChange}
              rows={3}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-colors text-gray-900 ${
                errors.assunto ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Descreva o que você precisa"
            />
            {errors.assunto && <p className="mt-1 text-sm text-red-500">{errors.assunto}</p>}
          </div>
          
          <motion.button
            type="submit"
            className="w-full bg-accent hover:bg-accent/90 text-white font-semibold py-3 px-6 rounded-md transition-colors flex items-center justify-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Send size={18} className="mr-2" />
            Enviar mensagem
          </motion.button>
          
          <p className="text-xs text-gray-500 text-center mt-4">
            Ao enviar, você será redirecionado para o WhatsApp com sua mensagem pronta para ser enviada.
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ContactForm;
